import React, { useState } from "react";
import { auth, db } from "../../firebase-config";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { ref, set } from "firebase/database";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";
import CryptoJS from "crypto-js"; // Used for admin key generation
import "./AddAdmin.css";
import { MuiTelInput } from "mui-tel-input";

const AddAdmin = () => {
  // Currency state

  // Helper for symbol
  const [currency, setCurrency] = useState("INR");
  const currencySymbol = currency === "INR" ? "₹" : "$";
  // Basic info
  const [fullName, setFullName] = useState("");
  const [contact, setContact] = useState("");
  const [role] = useState("Admin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Costing & license
  const [costingType, setCostingType] = useState("");
  const [generateLicense, setGenerateLicense] = useState(false);
  const [selectedExpiry, setSelectedExpiry] = useState("");

  // Pricing fields
  const [costPerView, setCostPerView] = useState("");
  const [mriPricing, setMriPricing] = useState("");
  const [ctPricing, setCtPricing] = useState("");
  const [xrayPricing, setXrayPricing] = useState("");
  const [prepaidAmount, setPrepaidAmount] = useState("");

  // AI model enable flags (Prepaid)
  const [enableCxrTb, setEnableCxrTb] = useState(false);
  const [enableCxrTb2, setEnableCxrTb2] = useState(false);
  const [enableCxrMulticlass, setEnableCxrMulticlass] = useState(false);
  const [enableXrayFracture, setEnableXrayFracture] = useState(false);
  const [enable3DAbdomen, setEnable3DAbdomen] = useState(false);
  const [enable3DHaN, setEnable3DHaN] = useState(false);
  const [enableContourAbdomen, setEnableContourAbdomen] = useState(false);
  const [enableContourHaN, setEnableContourHaN] = useState(false);
  const [enableTumorAbdomen, setEnableTumorAbdomen] = useState(false);
  const [enableLiverSegmentation, setEnableLiverSegmentation] = useState(false);

  // AUTO RUN enable flags for AI models
  const [autoRunCxrTb, setAutoRunCxrTb] = useState(false);
  const [autoRunCxrTb2, setAutoRunCxrTb2] = useState(false);
  const [autoRunCxrMulticlass, setAutoRunCxrMulticlass] = useState(false);
  const [autoRunXrayFracture, setAutoRunXrayFracture] = useState(false);
  const [autoRun3DAbdomen, setAutoRun3DAbdomen] = useState(false);
  const [autoRun3DHaN, setAutoRun3DHaN] = useState(false);
  const [autoRunContourAbdomen, setAutoRunContourAbdomen] = useState(false);
  const [autoRunContourHaN, setAutoRunContourHaN] = useState(false);
  const [autoRunTumorAbdomen, setAutoRunTumorAbdomen] = useState(false);
  const [autoRunLiverSegmentation, setAutoRunLiverSegmentation] = useState(false);

  // AI model pricing fields
  const [cxrTbModel, setCxrTbModel] = useState("");
  const [cxrTb2Model, setCxrTb2Model] = useState("");
  const [cxrMulticlassModel, setCxrMulticlassModel] = useState("");
  const [xrayFractureModel, setXrayFractureModel] = useState("");
  const [threeDAbdomenModel, setThreeDAbdomenModel] = useState("");
  const [threeDHaNModel, setThreeDHaNModel] = useState("");
  const [contourAbdomenModel, setContourAbdomenModel] = useState("");
  const [contourHaNModel, setContourHaNModel] = useState("");
  const [tumorAbdomenModel, setTumorAbdomenModel] = useState("");
  const [liverSegmentationModel, setLiverSegmentationModel] = useState("");

  const navigate = useNavigate();

  const notifyWarning = (error) => {
    toast.warn(error.message || "An error occurred during registration.", {
      position: "top-center",
    });
  };

  const notifySuccess = () => {
    toast.success("Admin added successfully", {
      position: "top-center",
    });
  };

  // Automatically generate a unique Admin Key
  const generateAdminKey = () => {
    return CryptoJS.SHA256(new Date().toISOString()).toString();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // 1) Trim and validate full name
    const sanitizedFullName = fullName.trim();
    if (!sanitizedFullName) {
      toast.error("Please provide a valid Full Name.", {
        position: "top-center",
      });
      return;
    }

    // 2) Create the Firebase Auth user
    createUserWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        // 3) Generate admin key and write base admin data
        const generatedAdminKey = generateAdminKey();
        const adminRef = ref(db, `superadmin/admins/${sanitizedFullName}`);
        set(adminRef, {
          FullName: sanitizedFullName,
          Password: password,
          Email: email,
          Role: role,
          CostingType: costingType,
          Currency: currency,
          Contact: contact ? contact.replace(/\s+/g, "") : "",
          AdminKey: generatedAdminKey,
        });

        // 4) Build base costing data according to costingType
        let baseCosting = {};
        
        // Add Patient Enrollment fee for all costing types
        if (prepaidAmount) {
          baseCosting.PatientAddFee = prepaidAmount;
        }
        
        // Add specific costing data according to costingType
        if (costingType === "Prepaid") {
          // PatientAddFee already added above
        } else if (
          costingType === "Cost Per Upload" ||
          costingType === "Cost Per Upload + View"
        ) {
          baseCosting.CPU = {
            MRIPricing: mriPricing,
            CTPricing: ctPricing,
            XrayPricing: xrayPricing,
          };
          if (costingType === "Cost Per Upload + View" && costPerView) {
            baseCosting.CPV = costPerView;
          }
        } else if (costingType === "Cost Per View") {
          baseCosting.CPV = costPerView;
        }

        // 5) Build AI model pricing for all types
        const modelPricing = {};
        if (enableCxrTb) modelPricing["CXR-TB"] = cxrTbModel;
        if (enableCxrTb2) modelPricing["CXR-TB2"] = cxrTb2Model;
        if (enableCxrMulticlass)
          modelPricing["CXR-Multiclass"] = cxrMulticlassModel;
        if (enableXrayFracture)
          modelPricing["XRay-Fracture"] = xrayFractureModel;
        if (enable3DAbdomen) modelPricing["3D-Abdomen"] = threeDAbdomenModel;
        if (enable3DHaN) modelPricing["3D-HaN"] = threeDHaNModel;
        if (enableContourAbdomen)
          modelPricing["Contour-Abdomen"] = contourAbdomenModel;
        if (enableContourHaN) modelPricing["Contour-HaN"] = contourHaNModel;
        if (enableTumorAbdomen)
          modelPricing["Tumor-Abdomen"] = tumorAbdomenModel;
        if (enableLiverSegmentation)
          modelPricing["Liver-Segmentation"] = liverSegmentationModel;

        // 6) Write combined costing data
        set(ref(db, `superadmin/admins/${sanitizedFullName}/costing`), {
          ...baseCosting,
          ...modelPricing,
        });

        // 7) Build and write AI_Model_Enabled flags
        const enabledModels = {};
        if (enableCxrTb) enabledModels["CXR-TB"] = true;
        if (enableCxrTb2) enabledModels["CXR-TB2"] = true;
        if (enableCxrMulticlass) enabledModels["CXR-Multiclass"] = true;
        if (enableXrayFracture) enabledModels["XRay-Fracture"] = true;
        if (enable3DAbdomen) enabledModels["3D-Abdomen"] = true;
        if (enable3DHaN) enabledModels["3D-HaN"] = true;
        if (enableContourAbdomen) enabledModels["Contour-Abdomen"] = true;
        if (enableContourHaN) enabledModels["Contour-HaN"] = true;
        if (enableTumorAbdomen) enabledModels["Tumor-Abdomen"] = true;
        if (enableLiverSegmentation) enabledModels["Liver-Segmentation"] = true;

        set(
          ref(db, `superadmin/admins/${sanitizedFullName}/AI_Model_Enabled`),
          enabledModels
        );

        // 8) Build and write AI_Model_AutoRun flags
        const autoRunModels = {};
        if (autoRunCxrTb) autoRunModels["CXR-TB"] = true;
        if (autoRunCxrTb2) autoRunModels["CXR-TB2"] = true;
        if (autoRunCxrMulticlass) autoRunModels["CXR-Multiclass"] = true;
        if (autoRunXrayFracture) autoRunModels["XRay-Fracture"] = true;
        if (autoRun3DAbdomen) autoRunModels["3D-Abdomen"] = true;
        if (autoRun3DHaN) autoRunModels["3D-HaN"] = true;
        if (autoRunContourAbdomen) autoRunModels["Contour-Abdomen"] = true;
        if (autoRunContourHaN) autoRunModels["Contour-HaN"] = true;
        if (autoRunTumorAbdomen) autoRunModels["Tumor-Abdomen"] = true;
        if (autoRunLiverSegmentation) autoRunModels["Liver-Segmentation"] = true;

        set(
          ref(db, `superadmin/admins/${sanitizedFullName}/AI_Model_AutoRun`),
          autoRunModels
        );

        // 8) Optionally generate and save a license
        if (generateLicense && selectedExpiry) {
          const licenseKey = generateLicenseKey();
          const issueDate = formatDate(new Date());
          const expiryDate = calculateExpiryDate(parseInt(selectedExpiry, 10));

          set(
            ref(
              db,
              `superadmin/admins/${sanitizedFullName}/License Details/LicenseKey`
            ),
            licenseKey
          );
          set(
            ref(
              db,
              `superadmin/admins/${sanitizedFullName}/License Details/IssueDate`
            ),
            issueDate
          );
          set(
            ref(
              db,
              `superadmin/admins/${sanitizedFullName}/License Details/ExpiryDate`
            ),
            expiryDate
          );
        }

        // 9) Notify success and reset form
        notifySuccess();
        e.target.reset();
        setTimeout(() => navigate("/SuperAdmin"), 3000);
      })
      .catch((error) => notifyWarning(error));
  };

  const generateLicenseKey = () => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const keyLength = 10;
    let licenseKey = "";
    for (let i = 0; i < keyLength; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      licenseKey += characters[randomIndex];
    }
    return licenseKey;
  };

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const calculateExpiryDate = (months) => {
    const currentDate = new Date();
    const expiryDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + months,
      currentDate.getDate()
    );
    return formatDate(expiryDate);
  };

  const renderCostingInput = () => {
    // If no costing type is selected, return null (don't show any fields)
    if (!costingType) {
      return null;
    }
    
    return (
      <>
        {/* Cost Per Upload & Cost Per Upload + View specific fields */}
        {(costingType === "Cost Per Upload" || costingType === "Cost Per Upload + View") && (
          <>
            <div>
              <strong className="strongaddadmin">MRI Pricing:</strong>
              <div className="input-group">
                <input
                  className="inputaddadmin inputaddadmin-CPV"
                  placeholder="Enter MRI Pricing"
                  value={mriPricing}
                  onChange={(e) => setMriPricing(e.target.value)}
                  type="number"
                />
                <span className="input-currency-symbol">{currencySymbol}</span>
              </div>
            </div>
            
            <div>
              <strong className="strongaddadmin">CT Pricing:</strong>
              <div className="input-group">
                <input
                  className="inputaddadmin inputaddadmin-CPV"
                  placeholder="Enter CT Pricing"
                  value={ctPricing}
                  onChange={(e) => setCtPricing(e.target.value)}
                  type="number"
                />
                <span className="input-currency-symbol">{currencySymbol}</span>
              </div>
            </div>
            
            <div>
              <strong className="strongaddadmin">X-ray Pricing:</strong>
              <div className="input-group">
                <input
                  className="inputaddadmin inputaddadmin-CPV"
                  placeholder="Enter X-ray Pricing"
                  value={xrayPricing}
                  onChange={(e) => setXrayPricing(e.target.value)}
                  type="number"
                />
                <span className="input-currency-symbol">{currencySymbol}</span>
              </div>
            </div>
          </>
        )}
        
        {/* Cost Per View & Cost Per Upload + View specific fields */}
        {(costingType === "Cost Per View" || costingType === "Cost Per Upload + View") && (
          <div>
            <strong className="strongaddadmin">Cost Per View:</strong>
            <div className="input-group">
              <input
                className="inputaddadmin inputaddadmin-CPV"
                placeholder="Enter CPV Amount"
                value={costPerView}
                onChange={(e) => setCostPerView(e.target.value)}
                type="number"
              />
              <span className="input-currency-symbol">{currencySymbol}</span>
            </div>
          </div>
        )}
      </>
    );
  };
  const renderAiModels = () => (
    <div className="AI_Models_Enabled">
      <strong className="strongaddadmin">Select AI Models:</strong>

      {/* CXR-TB */}
      <div className="model-selection">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <label>
            <input
              type="checkbox"
              checked={enableCxrTb}
              onChange={(e) => {
                setEnableCxrTb(e.target.checked);
                if (!e.target.checked) setAutoRunCxrTb(false);
              }}
            />
            CXR-TB
          </label>
          {enableCxrTb && (
            <label style={{ marginLeft: '20px' }}>
              <input
                type="checkbox"
                checked={autoRunCxrTb}
                onChange={(e) => setAutoRunCxrTb(e.target.checked)}
              />
              AUTO RUN
            </label>
          )}
        </div>
        {enableCxrTb && (
          <div className="input-group">
            <input
              className="inputaddadmin inputaddadmin-CPV"
              placeholder="Enter Price"
              value={cxrTbModel}
              onChange={(e) => setCxrTbModel(e.target.value)}
              type="number"
            />
            <span className="input-currency-symbol">{currencySymbol}</span>
          </div>
        )}
      </div>

      {/* CXR-TB2 */}
      <div className="model-selection">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <label>
            <input
              type="checkbox"
              checked={enableCxrTb2}
              onChange={(e) => {
                setEnableCxrTb2(e.target.checked);
                if (!e.target.checked) setAutoRunCxrTb2(false);
              }}
            />
            CXR-TB2
          </label>
          {enableCxrTb2 && (
            <label style={{ marginLeft: '20px' }}>
              <input
                type="checkbox"
                checked={autoRunCxrTb2}
                onChange={(e) => setAutoRunCxrTb2(e.target.checked)}
              />
              AUTO RUN
            </label>
          )}
        </div>
        {enableCxrTb2 && (
          <div className="input-group">
            <input
              className="inputaddadmin inputaddadmin-CPV"
              placeholder="Enter Price"
              value={cxrTb2Model}
              onChange={(e) => setCxrTb2Model(e.target.value)}
              type="number"
            />
            <span className="input-currency-symbol">{currencySymbol}</span>
          </div>
        )}
      </div>

      {/* CXR-Multiclass */}
      <div className="model-selection">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <label>
            <input
              type="checkbox"
              checked={enableCxrMulticlass}
              onChange={(e) => {
                setEnableCxrMulticlass(e.target.checked);
                if (!e.target.checked) setAutoRunCxrMulticlass(false);
              }}
            />
            CXR-Multiclass
          </label>
          {enableCxrMulticlass && (
            <label style={{ marginLeft: '20px' }}>
              <input
                type="checkbox"
                checked={autoRunCxrMulticlass}
                onChange={(e) => setAutoRunCxrMulticlass(e.target.checked)}
              />
              AUTO RUN
            </label>
          )}
        </div>
        {enableCxrMulticlass && (
          <div className="input-group">
            <input
              className="inputaddadmin inputaddadmin-CPV"
              placeholder="Enter Price"
              value={cxrMulticlassModel}
              onChange={(e) => setCxrMulticlassModel(e.target.value)}
              type="number"
            />
            <span className="input-currency-symbol">{currencySymbol}</span>
          </div>
        )}
      </div>

      {/* XRay-Fracture */}
      <div className="model-selection">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <label>
            <input
              type="checkbox"
              checked={enableXrayFracture}
              onChange={(e) => {
                setEnableXrayFracture(e.target.checked);
                if (!e.target.checked) setAutoRunXrayFracture(false);
              }}
            />
            XRay-Fracture
          </label>
          {enableXrayFracture && (
            <label style={{ marginLeft: '20px' }}>
              <input
                type="checkbox"
                checked={autoRunXrayFracture}
                onChange={(e) => setAutoRunXrayFracture(e.target.checked)}
              />
              AUTO RUN
            </label>
          )}
        </div>
        {enableXrayFracture && (
          <div className="input-group">
            <input
              className="inputaddadmin inputaddadmin-CPV"
              placeholder="Enter Price"
              value={xrayFractureModel}
              onChange={(e) => setXrayFractureModel(e.target.value)}
              type="number"
            />
            <span className="input-currency-symbol">{currencySymbol}</span>
          </div>
        )}
      </div>

      {/* 3D-Abdomen */}
      <div className="model-selection">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <label>
            <input
              type="checkbox"
              checked={enable3DAbdomen}
              onChange={(e) => {
                setEnable3DAbdomen(e.target.checked);
                if (!e.target.checked) setAutoRun3DAbdomen(false);
              }}
            />
            3D-Abdomen
          </label>
          {enable3DAbdomen && (
            <label style={{ marginLeft: '20px' }}>
              <input
                type="checkbox"
                checked={autoRun3DAbdomen}
                onChange={(e) => setAutoRun3DAbdomen(e.target.checked)}
              />
              AUTO RUN
            </label>
          )}
        </div>
        {enable3DAbdomen && (
          <div className="input-group">
            <input
              className="inputaddadmin inputaddadmin-CPV"
              placeholder="Enter Price"
              value={threeDAbdomenModel}
              onChange={(e) => setThreeDAbdomenModel(e.target.value)}
              type="number"
            />
            <span className="input-currency-symbol">{currencySymbol}</span>
          </div>
        )}
      </div>

      {/* 3D-HaN */}
      <div className="model-selection">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <label>
            <input
              type="checkbox"
              checked={enable3DHaN}
              onChange={(e) => {
                setEnable3DHaN(e.target.checked);
                if (!e.target.checked) setAutoRun3DHaN(false);
              }}
            />
            3D-HaN
          </label>
          {enable3DHaN && (
            <label style={{ marginLeft: '20px' }}>
              <input
                type="checkbox"
                checked={autoRun3DHaN}
                onChange={(e) => setAutoRun3DHaN(e.target.checked)}
              />
              AUTO RUN
            </label>
          )}
        </div>
        {enable3DHaN && (
          <div className="input-group">
            <input
              className="inputaddadmin inputaddadmin-CPV"
              placeholder="Enter Price"
              value={threeDHaNModel}
              onChange={(e) => setThreeDHaNModel(e.target.value)}
              type="number"
            />
            <span className="input-currency-symbol">{currencySymbol}</span>
          </div>
        )}
      </div>

      {/* Contour-Abdomen */}
      <div className="model-selection">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <label>
            <input
              type="checkbox"
              checked={enableContourAbdomen}
              onChange={(e) => {
                setEnableContourAbdomen(e.target.checked);
                if (!e.target.checked) setAutoRunContourAbdomen(false);
              }}
            />
            Contour-Abdomen
          </label>
          {enableContourAbdomen && (
            <label style={{ marginLeft: '20px' }}>
              <input
                type="checkbox"
                checked={autoRunContourAbdomen}
                onChange={(e) => setAutoRunContourAbdomen(e.target.checked)}
              />
              AUTO RUN
            </label>
          )}
        </div>
        {enableContourAbdomen && (
          <div className="input-group">
            <input
              className="inputaddadmin inputaddadmin-CPV"
              placeholder="Enter Price"
              value={contourAbdomenModel}
              onChange={(e) => setContourAbdomenModel(e.target.value)}
              type="number"
            />
            <span className="input-currency-symbol">{currencySymbol}</span>
          </div>
        )}
      </div>

      {/* Contour-HaN */}
      <div className="model-selection">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <label>
            <input
              type="checkbox"
              checked={enableContourHaN}
              onChange={(e) => {
                setEnableContourHaN(e.target.checked);
                if (!e.target.checked) setAutoRunContourHaN(false);
              }}
            />
            Contour-HaN
          </label>
          {enableContourHaN && (
            <label style={{ marginLeft: '20px' }}>
              <input
                type="checkbox"
                checked={autoRunContourHaN}
                onChange={(e) => setAutoRunContourHaN(e.target.checked)}
              />
              AUTO RUN
            </label>
          )}
        </div>
        {enableContourHaN && (
          <div className="input-group">
            <input
              className="inputaddadmin inputaddadmin-CPV"
              placeholder="Enter Price"
              value={contourHaNModel}
              onChange={(e) => setContourHaNModel(e.target.value)}
              type="number"
            />
            <span className="input-currency-symbol">{currencySymbol}</span>
          </div>
        )}
      </div>

      {/* Tumor-Abdomen */}
      <div className="model-selection">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <label>
            <input
              type="checkbox"
              checked={enableTumorAbdomen}
              onChange={(e) => {
                setEnableTumorAbdomen(e.target.checked);
                if (!e.target.checked) setAutoRunTumorAbdomen(false);
              }}
            />
            Tumor-Abdomen
          </label>
          {enableTumorAbdomen && (
            <label style={{ marginLeft: '20px' }}>
              <input
                type="checkbox"
                checked={autoRunTumorAbdomen}
                onChange={(e) => setAutoRunTumorAbdomen(e.target.checked)}
              />
              AUTO RUN
            </label>
          )}
        </div>
        {enableTumorAbdomen && (
          <div className="input-group">
            <input
              className="inputaddadmin inputaddadmin-CPV"
              placeholder="Enter Price"
              value={tumorAbdomenModel}
              onChange={(e) => setTumorAbdomenModel(e.target.value)}
              type="number"
            />
            <span className="input-currency-symbol">{currencySymbol}</span>
          </div>
        )}
      </div>

      {/* Liver-Segmentation */}
      <div className="model-selection">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <label>
            <input
              type="checkbox"
              checked={enableLiverSegmentation}
              onChange={(e) => {
                setEnableLiverSegmentation(e.target.checked);
                if (!e.target.checked) setAutoRunLiverSegmentation(false);
              }}
            />
            Liver-Segmentation
          </label>
          {enableLiverSegmentation && (
            <label style={{ marginLeft: '20px' }}>
              <input
                type="checkbox"
                checked={autoRunLiverSegmentation}
                onChange={(e) => setAutoRunLiverSegmentation(e.target.checked)}
              />
              AUTO RUN
            </label>
          )}
        </div>
        {enableLiverSegmentation && (
          <div className="input-group">
            <input
              className="inputaddadmin inputaddadmin-CPV"
              placeholder="Enter Price"
              value={liverSegmentationModel}
              onChange={(e) => setLiverSegmentationModel(e.target.value)}
              type="number"
            />
            <span className="input-currency-symbol">{currencySymbol}</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <h2 className="text-center h2addadmin">ADD ADMIN</h2>
      {/* <Link to="/SuperAdmin" className="homeButton">
        <span id="backText" className="backText">
          Back
        </span>
        <i id="backIcon" className="fas fa-long-arrow-alt-left fa-lg fa-2x"></i>
      </Link> */}
      <form className="addAdminForm" onSubmit={handleSubmit}>
        <ToastContainer />
        <strong className="strongaddadmin">Full Name:</strong>
        <input
          className="inputaddadmin"
          type="text"
          placeholder="Enter Full Name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
        />
        <strong className="strongaddadmin">Email:</strong>
        <input
          className="inputaddadmin"
          placeholder="Enter Your Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          type="email"
        />
        <strong className="strongaddadmin">Password:</strong>
        <input
          className="inputaddadmin"
          placeholder="Enter Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          type="password"
        />

        <strong className="strongaddadmin">Contact:</strong>
        <MuiTelInput
          value={contact}
          onChange={setContact}
          defaultCountry="IN"
          id="contact"
          name="contact"
          placeholder="Enter contact number (optional)"
          className="inputaddadmin"
          inputProps={{ className: "inputPN" }}
        />

        {/* Currency selection dropdown */}
        <strong className="strongaddadmin">Currency:</strong>
        <select
          className="inputaddadmin"
          value={currency}
          onChange={e => setCurrency(e.target.value)}
          style={{ marginBottom: '16px' }}
          required
        >
          <option value="" disabled>Choose Currency</option>
          <option value="INR">INR (₹)</option>
          <option value="USD">USD ($)</option>
        </select>

        <strong className="strongaddadmin">Costing Type:</strong>
        <select
          className="inputaddadmin"
          value={costingType}
          onChange={(e) => setCostingType(e.target.value)}
          required
        >
          <option value="">Select Costing Type</option>
          <option value="Cost Per Upload">Cost Per Upload</option>
          <option value="Cost Per View">Cost Per View</option>
          <option value="Cost Per Upload + View">Cost Per Upload + View</option>
          <option value="Prepaid">Prepaid</option>
        </select>
        {renderCostingInput()}
        
        {/* Patient Enrollment field - appears right before AI Models */}
        {costingType && (
          <div>
            <strong className="strongaddadmin">Patient Enrollment:</strong>
            <div className="input-group">
              <input
                className="inputaddadmin inputaddadmin-CPV"
                placeholder="Enter Amount"
                value={prepaidAmount}
                onChange={(e) => setPrepaidAmount(e.target.value)}
                type="number"
              />
              <span className="input-currency-symbol">{currencySymbol}</span>
            </div>
          </div>
        )}
        
        {costingType && renderAiModels()}

        <div className="checkbox-container">
          <input
            type="checkbox"
            className="custom-checkbox"
            id="generateLicense"
            checked={generateLicense}
            onChange={(e) => setGenerateLicense(e.target.checked)}
          />
          <label
            htmlFor="generateLicense"
            style={{ marginLeft: "8px", fontSize: "20px" }}
          >
            Generate License
          </label>
        </div>
        {generateLicense && (
          <div>
            <select
              className="expiry-dropdown"
              value={selectedExpiry}
              onChange={(e) => setSelectedExpiry(e.target.value)}
            >
              <option value="" disabled>
                Select Expiry Duration
              </option>
              <option value={1}>Trial License</option>
              <option value={3}>3 Months</option>
              <option value={6}>6 Months</option>
              <option value={12}>1 Year</option>
              <option value={36}>3 Years</option>
              <option value={60}>5 Years</option>
            </select>
          </div>
        )}
        <button className="addadminbtn">Add Admin</button>
      </form>
    </div>
  );
};

export default AddAdmin;
